// PartnerApply · combined calculator + application form in a single
// wide section that matches the partner-page header width. The
// sliders configure projected earnings on the LEFT; the form on the
// RIGHT captures the partner's details. On submit, the projected
// earnings + sizing inputs are packed into the email body sent to
// support@ofinancial.markets so the partnership team can see at a
// glance what the applicant modelled.
import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Calculator, TrendingUp, Users, BarChart3,
  ArrowRight, CheckCircle2, Handshake, Mail, User, Globe, BadgeCheck,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
// Industry-standard EURUSD Classic account economics used for the
// projection · spread on Classic ≈ 1.0 pip ⇒ $10 per standard lot,
// 50% partner share → $5 per lot. Estimate, not a guarantee.
const REVENUE_PER_LOT_USD = 5;

const PROGRAMMES = [
  { id: "introducer", label: "Introducer (IB)", Icon: Handshake },
  { id: "ambassador", label: "Ambassador",      Icon: BadgeCheck },
  { id: "custom",     label: "Custom · talk to us", Icon: Users },
];

function MetricRow({ label, value, unit, Icon, color }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="relative w-8 h-8 grid place-items-center shrink-0">
        <div aria-hidden className="absolute inset-0 rounded-full"
             style={{ background: `radial-gradient(circle, color-mix(in srgb, ${color} 18%, transparent) 0%, transparent 70%)`, filter: "blur(2px)" }} />
        <Icon size={15} strokeWidth={1.7} style={{ color, position: "relative", zIndex: 1 }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-mono text-[12px] uppercase tracking-[0.2em] text-text-3">{label}</div>
        <div className="font-display text-[15px] font-semibold tabular-nums text-text-1 leading-tight">
          {value} <span className="font-mono text-[12px] uppercase tracking-[0.16em] text-text-3 ml-0.5">{unit}</span>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, Icon, children }) {
  return (
    <div>
      <label className="block font-mono text-[12px] uppercase tracking-[0.2em] text-text-3 mb-1.5">{label}</label>
      <div className="relative">
        {Icon && <Icon size={14} strokeWidth={1.8} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-3 pointer-events-none" />}
        {children}
      </div>
    </div>
  );
}

export default function PartnerApply() {
  const { t } = useTranslation();
  const [clients, setClients] = useState(20);
  const [lots, setLots] = useState(15);
  const [form, setForm] = useState({
    name: "",
    email: "",
    country: "",
    programme: "introducer",
    audience_channels: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const monthly = useMemo(() => clients * lots * REVENUE_PER_LOT_USD, [clients, lots]);
  const yearly = monthly * 12;
  const totalLots = clients * lots;

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      // Build a structured message body so the partnership team sees
      // both the applicant's stated network AND their own projection
      // in one glance · this is the whole point of combining the
      // calculator and form.
      const programmeLabel = PROGRAMMES.find(p => p.id === form.programme)?.label ?? form.programme;
      const message = [
        `Programme: ${programmeLabel}`,
        ``,
        `── Projected network (from calculator) ──`,
        `Active referral clients: ${clients}`,
        `Avg lots / trader / month: ${lots}`,
        `Total monthly volume: ${totalLots.toLocaleString()} lots`,
        `Projected monthly earnings: $${monthly.toLocaleString()}`,
        `Projected annualised: $${yearly.toLocaleString()}`,
        ``,
        `── Applicant details ──`,
        `Country: ${form.country}`,
        `Audience / channels: ${form.audience_channels}`,
      ].join("\n");

      const usp = new URLSearchParams(window.location.search);
      const res = await fetch(`${API}/api/forms/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source: "partner",
          name: form.name,
          email: form.email,
          phone: "",
          subject: `Partner application · ${programmeLabel} · $${monthly.toLocaleString()}/mo projected`,
          message,
          page_url:     window.location.pathname,
          utm_source:   usp.get("utm_source")   || undefined,
          utm_medium:   usp.get("utm_medium")   || undefined,
          utm_campaign: usp.get("utm_campaign") || undefined,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDone(true);
    } catch (err) {
      setError(t("partner.apply.error", "Could not submit. Please try again or email support@ofinancial.markets directly."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="relative py-12 lg:py-16 scroll-mt-24" id="apply" data-testid="partner-apply">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-8 lg:mb-10">
          <div className="pill-mono inline-flex mb-3">
            {t("partner.combined.pill", "Size your network · apply in 60 seconds")}
          </div>
          <h2 className="font-display text-2xl/[1.25] lg:text-3xl/[1.25] font-medium tracking-[-0.02em] text-text-1 mb-3">
            {t("partner.combined.title", "Become an OFinancial")}{" "}
            <span className="text-gradient-brand">{t("partner.combined.titleAccent", "partner")}</span>
          </h2>
          <p className="text-text-3 text-[14.5px] lg:text-[15px] font-light max-w-2xl mx-auto">
            {t("partner.combined.subtitle", "Drag the sliders to model your expected network, then send your details · our partnership team responds within one business day with your dashboard and payout setup.")}
          </p>
        </div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="card-beam p-10 lg:p-14 text-center max-w-2xl mx-auto"
            data-testid="partner-apply-success"
          >
            <div
              className="w-16 h-16 rounded-full grid place-items-center mx-auto mb-5"
              style={{ background: "color-mix(in srgb, var(--brand) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--brand) 12%, transparent)" }}
            >
              <CheckCircle2 size={30} strokeWidth={1.7} style={{ color: "var(--brand)" }} />
            </div>
            <h3 className="font-display text-2xl font-medium text-text-1 mb-3">
              {t("partner.apply.successTitle", "Application received")}
            </h3>
            <p className="text-text-3 text-[14.5px] font-light max-w-md mx-auto">
              {t("partner.apply.successBody", "Thanks — our partnership team has it. Expect a response within one business day. Watch your inbox for follow-up from support@ofinancial.markets.")}
            </p>
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full font-mono text-[12px] uppercase tracking-[0.22em]"
                 style={{ background: "color-mix(in srgb, var(--brand) 6%, transparent)", color: "var(--text-2)", border: "1px solid color-mix(in srgb, var(--brand) 12%, transparent)" }}>
              <TrendingUp size={11} style={{ color: "var(--brand)" }} />
              {t("partner.combined.summary", "Network you modelled")}: {clients} × {lots} lots · ${monthly.toLocaleString()}/mo
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10% 0px" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="card-beam overflow-hidden grid lg:grid-cols-[1fr_1fr] !p-0"
          >
            {/* ===== LEFT · Calculator ===== */}
            <div
              className="p-6 lg:p-8 flex flex-col"
              style={{
                background: "linear-gradient(180deg, color-mix(in srgb, var(--brand) 4%, transparent), color-mix(in srgb, var(--brand) 1%, transparent))",
                borderRight: "1px solid color-mix(in srgb, var(--brand) 8%, var(--line))",
              }}
            >
              <div className="flex items-center gap-2 mb-5">
                <Calculator size={14} style={{ color: "var(--brand)" }} />
                <span className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3">
                  {t("partner.combined.configure", "1 · Model your network")}
                </span>
              </div>

              {/* Slider · active clients */}
              <div className="mb-6">
                <div className="flex items-end justify-between mb-2">
                  <label htmlFor="apply-clients" className="font-mono text-[12px] uppercase tracking-[0.2em] text-text-3">
                    {t("partner.calc.clientsLabel", "Active referral clients")}
                  </label>
                  <span className="font-display text-[22px] font-semibold tabular-nums text-text-1 leading-none">
                    {clients} <span className="font-mono text-[12px] uppercase tracking-[0.16em] text-text-3 ml-1">{t("partner.calc.tradersUnit", "traders")}</span>
                  </span>
                </div>
                <input
                  id="apply-clients"
                  type="range"
                  min="1" max="500" step="1"
                  value={clients}
                  onChange={(e) => setClients(Number(e.target.value))}
                  className="w-full accent-[color:var(--brand)] cursor-pointer"
                  data-testid="partner-apply-clients"
                />
                <div className="flex justify-between font-mono text-[12px] uppercase tracking-[0.16em] text-text-3 mt-1">
                  <span>1</span><span>500</span>
                </div>
              </div>

              {/* Slider · avg lots */}
              <div className="mb-5">
                <div className="flex items-end justify-between mb-2">
                  <label htmlFor="apply-lots" className="font-mono text-[12px] uppercase tracking-[0.2em] text-text-3">
                    {t("partner.calc.lotsLabel", "Avg trading volume per trader · monthly")}
                  </label>
                  <span className="font-display text-[22px] font-semibold tabular-nums text-text-1 leading-none">
                    {lots} <span className="font-mono text-[12px] uppercase tracking-[0.16em] text-text-3 ml-1">{t("partner.calc.lotsUnit", "lots")}</span>
                  </span>
                </div>
                <input
                  id="apply-lots"
                  type="range"
                  min="1" max="100" step="1"
                  value={lots}
                  onChange={(e) => setLots(Number(e.target.value))}
                  className="w-full accent-[color:var(--brand)] cursor-pointer"
                  data-testid="partner-apply-lots"
                />
                <div className="flex justify-between font-mono text-[12px] uppercase tracking-[0.16em] text-text-3 mt-1">
                  <span>1</span><span>100</span>
                </div>
              </div>

              {/* Quick metrics row */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <MetricRow label={t("partner.calc.networkSize", "Network size")} value={clients.toLocaleString()} unit={t("partner.calc.tradersUnit", "traders")} Icon={Users} color="#7be0ff" />
                <MetricRow label={t("partner.calc.totalVolume", "Total monthly volume")} value={totalLots.toLocaleString()} unit={t("partner.calc.lotsUnit", "lots")} Icon={BarChart3} color="#54d97c" />
              </div>

              {/* Result · big number */}
              <div
                className="mt-auto rounded-xl p-4 lg:p-5"
                style={{
                  background: "linear-gradient(180deg, color-mix(in srgb, var(--brand) 8%, transparent), color-mix(in srgb, var(--brand) 2%, transparent))",
                  border: "1px solid color-mix(in srgb, var(--brand) 10%, transparent)",
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp size={13} style={{ color: "var(--brand)" }} />
                  <span className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3">
                    {t("partner.calc.monthly", "Projected monthly earnings")}
                  </span>
                </div>
                <div
                  className="font-display font-semibold tabular-nums leading-none text-gradient-brand"
                  style={{ fontSize: "clamp(36px, 5vw, 52px)", letterSpacing: "-0.025em" }}
                >
                  ${monthly.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div>
                    <div className="font-mono text-[12px] uppercase tracking-[0.18em] text-text-3">{t("partner.calc.annualised", "Annualised")}</div>
                    <div className="font-display text-[15px] font-semibold tabular-nums text-text-1 leading-tight">${yearly.toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[12px] uppercase tracking-[0.18em] text-text-3">{t("partner.calc.perTrader", "Per trader / mo")}</div>
                    <div className="font-display text-[15px] font-semibold tabular-nums text-text-1 leading-tight">${(lots * 5).toLocaleString()}</div>
                  </div>
                </div>
                <p className="text-text-3 text-[12px] font-light leading-relaxed mt-3 border-t pt-3" style={{ borderColor: "color-mix(in srgb, var(--brand) 8%, var(--line))" }}>
                  {t("partner.calc.disclaimer", "Estimate based on EURUSD on a Classic account at the published 50 % spread revenue share. Actual earnings vary by instrument, account type and client activity.")}
                </p>
              </div>
            </div>

            {/* ===== RIGHT · Apply form ===== */}
            <form onSubmit={onSubmit} className="p-6 lg:p-8 space-y-4" data-testid="partner-apply-form">
              <div className="flex items-center gap-2 mb-1">
                <Mail size={14} style={{ color: "var(--brand)" }} />
                <span className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3">
                  {t("partner.combined.apply", "2 · Tell us about you")}
                </span>
              </div>

              {/* Programme selector · pill row */}
              <div>
                <label className="block font-mono text-[12px] uppercase tracking-[0.2em] text-text-3 mb-2">
                  {t("partner.apply.programmeLabel", "Programme of interest")}
                </label>
                <div className="flex flex-wrap gap-2">
                  {PROGRAMMES.map((p) => {
                    const active = form.programme === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setForm({ ...form, programme: p.id })}
                        className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[12px] font-medium transition-all hover:-translate-y-0.5"
                        style={
                          active
                            ? { background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: "var(--brand)", border: "1px solid color-mix(in srgb, var(--brand) 22%, transparent)" }
                            : { background: "var(--surface-2)", color: "var(--text-2)", border: "1px solid var(--line)" }
                        }
                        data-testid={`partner-apply-prog-${p.id}`}
                      >
                        <p.Icon size={13} />
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormField label={t("partner.apply.nameLabel", "Full name")} Icon={User}>
                  <input
                    required
                    name="name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder={t("partner.apply.namePh", "Jane Smith")}
                    className="w-full h-11 pl-9 pr-3 rounded-xl border bg-[color:var(--surface-2)] text-text-1 text-[14px] focus:outline-none focus:border-[color:var(--brand)] transition-colors"
                    style={{ borderColor: "var(--line)" }}
                    data-testid="partner-apply-name"
                  />
                </FormField>
                <FormField label={t("partner.apply.emailLabel", "Email")} Icon={Mail}>
                  <input
                    required
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane@example.com"
                    className="w-full h-11 pl-9 pr-3 rounded-xl border bg-[color:var(--surface-2)] text-text-1 text-[14px] focus:outline-none focus:border-[color:var(--brand)] transition-colors"
                    style={{ borderColor: "var(--line)" }}
                    data-testid="partner-apply-email"
                  />
                </FormField>
              </div>

              <FormField label={t("partner.apply.countryLabel", "Country / region")} Icon={Globe}>
                <input
                  required
                  name="country"
                  value={form.country}
                  onChange={update("country")}
                  placeholder={t("partner.apply.countryPh", "United Kingdom")}
                  className="w-full h-11 pl-9 pr-3 rounded-xl border bg-[color:var(--surface-2)] text-text-1 text-[14px] focus:outline-none focus:border-[color:var(--brand)] transition-colors"
                  style={{ borderColor: "var(--line)" }}
                  data-testid="partner-apply-country"
                />
              </FormField>

              <div>
                <label className="block font-mono text-[12px] uppercase tracking-[0.2em] text-text-3 mb-1.5">
                  {t("partner.apply.audienceLabel", "Audience / channels")}
                </label>
                <textarea
                  rows={3}
                  name="audience_channels"
                  value={form.audience_channels}
                  onChange={update("audience_channels")}
                  placeholder={t("partner.apply.audiencePh", "Where do you reach traders? (YouTube, Telegram, signal group, IB sub-network, etc.)")}
                  className="w-full p-3 rounded-xl border bg-[color:var(--surface-2)] text-text-1 text-[14px] focus:outline-none focus:border-[color:var(--brand)] transition-colors resize-none"
                  style={{ borderColor: "var(--line)" }}
                  data-testid="partner-apply-audience"
                />
              </div>

              {error && (
                <div className="text-[13px] py-2 px-3 rounded-lg" style={{ background: "color-mix(in srgb, var(--neg) 10%, transparent)", color: "var(--neg)", border: "1px solid color-mix(in srgb, var(--neg) 14%, transparent)" }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="btn-solid-brand w-full !h-12 !text-[12px] !uppercase !tracking-[0.2em] disabled:opacity-60"
                data-testid="partner-apply-submit"
              >
                {submitting
                  ? t("partner.apply.submitting", "Submitting...")
                  : t("partner.combined.submit", `Send application · $${monthly.toLocaleString()}/mo projected`)}
                {!submitting && <ArrowRight size={14} />}
              </button>

              <p className="text-text-3 text-[12px] font-light text-center pt-1">
                {t("partner.apply.privacy", "Your details go directly to our partnership team at support@ofinancial.markets. No spam.")}
              </p>
            </form>
          </motion.div>
        )}
      </div>
    </section>
  );
}
